import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  aggregateBenchmarkByInstitution,
  BENCHMARK_INSTITUTION_COLORS,
  formatBenchmarkStatValue,
} from "../../benchmarkMetricConfig";
import styles from "./GroupedMetricBars.module.scss";

const ROW_HEIGHT = 46;
const CHART_CHROME = 52; // axis + padding
const NAME_COL_WIDTH = 168;
const NAME_MAX_CHARS = 22; // per wrapped line
const NAME_MAX_LINES = 2;
const NAME_LINE_HEIGHT = 13;

/** Word-wrap a label to at most `maxLines`, ellipsizing the final line. */
const wrapLabel = (text, maxChars = NAME_MAX_CHARS, maxLines = NAME_MAX_LINES) => {
  const words = String(text ?? "").split(/\s+/).filter(Boolean);
  if (!words.length) return [""];
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxChars || !current) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  });
  if (current) lines.push(current);
  if (lines.length <= maxLines) return lines;
  const trimmed = lines.slice(0, maxLines);
  const last = trimmed[maxLines - 1];
  trimmed[maxLines - 1] = `${last.slice(0, Math.max(0, maxChars - 1))}…`;
  return trimmed;
};

/** Multi-line, right-aligned category tick so full entity names stay readable. */
const CategoryTick = ({ x, y, payload }) => {
  const lines = wrapLabel(payload?.value);
  const startY = y - ((lines.length - 1) * NAME_LINE_HEIGHT) / 2;
  return (
    <text
      x={x}
      y={startY}
      textAnchor="end"
      dominantBaseline="central"
      fill="#475569"
      fontSize={12}
      fontWeight={600}
    >
      {lines.map((line, idx) => (
        <tspan key={idx} x={x} dy={idx === 0 ? 0 : NAME_LINE_HEIGHT}>
          {line}
        </tspan>
      ))}
    </text>
  );
};

/**
 * One metric → a horizontal grouped bar chart with a separate full-length bar
 * per entity (institution / researcher / unit / venue), a value axis, gridlines
 * and an optional reference line (e.g. Specialization Index baseline at 1.0).
 * Unlike the proportional `StackedBarChart`, values are read on their own scale,
 * which is the right treatment for indices, ratios and percentages.
 */
const SingleMetricChart = ({ stat, entities, colorMap, exportMode = false }) => {
  const data = useMemo(
    () =>
      entities.map((e, idx) => ({
        id: e.institution_id,
        name: e.name || String(e.institution_id),
        value: Number(e[stat.key]) || 0,
        color:
          colorMap?.[e.institution_id] ||
          BENCHMARK_INSTITUTION_COLORS[idx % BENCHMARK_INSTITUTION_COLORS.length],
      })),
    [entities, stat.key, colorMap]
  );

  const minValue = data.reduce((m, d) => Math.min(m, d.value), 0);
  const height = data.length * ROW_HEIGHT + CHART_CHROME;

  return (
    // Export mode: fill the capture host's exact box (height:100%) so the chart
    // is drawn to the anchor dimensions rather than its intrinsic height.
    <div
      className={styles.card}
      style={
        exportMode
          ? { height: "100%", display: "flex", flexDirection: "column" }
          : undefined
      }
    >
      <div className={styles.title}>{stat.label}</div>
      <div style={exportMode ? { flex: 1, minHeight: 0 } : undefined}>
      <ResponsiveContainer width="100%" height={exportMode ? "100%" : height}>
        <BarChart
          layout="vertical"
          data={data}
          margin={{ top: 4, right: 52, bottom: 4, left: 4 }}
          barCategoryGap="28%"
        >
          <CartesianGrid horizontal={false} stroke="rgba(15,52,96,0.08)" />
          <XAxis
            type="number"
            domain={[Math.min(0, minValue), "auto"]}
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            tickLine={false}
            axisLine={{ stroke: "#e2e8f0" }}
            tickFormatter={(v) => formatBenchmarkStatValue(v, stat)}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={NAME_COL_WIDTH}
            tick={<CategoryTick />}
            tickLine={false}
            axisLine={false}
            interval={0}
          />
          <Tooltip
            cursor={{ fill: "rgba(15,52,96,0.05)" }}
            formatter={(value) => [formatBenchmarkStatValue(value, stat), stat.label]}
            contentStyle={{
              borderRadius: 10,
              border: "1px solid rgba(0,0,0,0.08)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              fontSize: 12,
            }}
          />
          {stat.referenceValue != null ? (
            <ReferenceLine
              x={stat.referenceValue}
              stroke="#ef4444"
              strokeDasharray="4 4"
              strokeWidth={1.5}
            />
          ) : null}
          <Bar
            dataKey="value"
            radius={[0, 6, 6, 0]}
            maxBarSize={26}
            isAnimationActive={false}
          >
            {data.map((d) => (
              <Cell key={d.id} fill={d.color} />
            ))}
            <LabelList
              dataKey="value"
              position="insideRight"
              formatter={(value) => formatBenchmarkStatValue(value, stat)}
              className={styles.valueLabel}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
};

/**
 * Renders a responsive grid of per-metric grouped bar charts for a set of
 * metrics, sharing the same per-entity color map used across the benchmark.
 */
const GroupedMetricBars = ({
  rawData,
  institutionIds,
  stats,
  colorMap,
  exportMode = false,
}) => {
  const entities = useMemo(
    () => aggregateBenchmarkByInstitution(rawData, institutionIds),
    [rawData, institutionIds]
  );

  const visibleStats = useMemo(
    () =>
      (stats || []).filter((stat) =>
        entities.some((e) => {
          const v = Number(e[stat.key]);
          return Number.isFinite(v) && v !== 0;
        })
      ),
    [stats, entities]
  );

  if (!entities.length || !visibleStats.length) return null;

  // Export mode (one chart per PPTX slot): skip the responsive grid and let the
  // single chart fill the capture host's box exactly.
  if (exportMode) {
    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {visibleStats.map((stat) => (
          <SingleMetricChart
            key={stat.key}
            stat={stat}
            entities={entities}
            colorMap={colorMap}
            exportMode
          />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.grid}>
      {visibleStats.map((stat) => (
        <SingleMetricChart
          key={stat.key}
          stat={stat}
          entities={entities}
          colorMap={colorMap}
        />
      ))}
    </div>
  );
};

export default GroupedMetricBars;
